import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { GenerationInput, GenerationPreview, Site } from './api';

interface User {
  id: string;
  email: string;
}

interface FormData extends Partial<GenerationInput> {
  step: number;
}

interface AppState {
  // Auth
  user: User | null;
  isAuthenticated: boolean;
  setUser: (user: User | null) => void;

  // Form
  formData: FormData;
  setFormData: (data: Partial<FormData>) => void;
  resetFormData: () => void;
  nextStep: () => void;
  prevStep: () => void;
  setStep: (step: number) => void;

  // Generation
  sessionId: string | null;
  previews: GenerationPreview[];
  selectedVersion: string | null;
  isGenerating: boolean;
  setSessionId: (id: string | null) => void;
  setPreviews: (previews: GenerationPreview[]) => void;
  setSelectedVersion: (version: string | null) => void;
  setIsGenerating: (isGenerating: boolean) => void;

  // Sites
  sites: Site[];
  setSites: (sites: Site[]) => void;
}

const initialFormData: FormData = {
  step: 1,
  businessName: '',
  industry: '',
  description: '',
  targetAudience: '',
  primaryColor: '#3f51b5',
  secondaryColor: '#ff4081',
  style: 'modern',
  sections: ['hero', 'features', 'contact'],
  contactEmail: '',
  websiteUrl: '',
  logoUrl: '',
  tagline: '',
  sectionDetails: {
    hero: {
      headline: '',
      subheadline: '',
      ctaText: '',
    },
    features: {
      sectionTitle: '',
      items: [{ title: '', description: '' }],
    },
    testimonials: {
      sectionTitle: '',
      items: [{ name: '', role: '', quote: '' }],
    },
    pricing: {
      sectionTitle: '',
      tiers: [{ name: '', price: '', period: '/month', features: [''], highlighted: false }],
    },
    faq: {
      sectionTitle: '',
      items: [{ question: '', answer: '' }],
    },
    contact: {
      sectionTitle: '',
      subtitle: '',
    },
  },
  customCopy: {
    headline: '',
    subheadline: '',
    ctaText: '',
  },
};

export const useStore = create<AppState>()(
  persist(
    (set) => ({
      // Auth
      user: null,
      isAuthenticated: false,
      setUser: (user) => set({ user, isAuthenticated: !!user }),

      // Form
      formData: initialFormData,
      setFormData: (data) =>
        set((state) => ({
          formData: { ...state.formData, ...data },
        })),
      resetFormData: () => set({ formData: initialFormData }),
      nextStep: () =>
        set((state) => ({
          formData: { ...state.formData, step: Math.min(state.formData.step + 1, 5) },
        })),
      prevStep: () =>
        set((state) => ({
          formData: { ...state.formData, step: Math.max(state.formData.step - 1, 1) },
        })),
      setStep: (step) =>
        set((state) => ({
          formData: { ...state.formData, step },
        })),

      // Generation
      sessionId: null,
      previews: [],
      selectedVersion: null,
      isGenerating: false,
      setSessionId: (sessionId) => set({ sessionId }),
      setPreviews: (previews) => set({ previews }),
      setSelectedVersion: (selectedVersion) => set({ selectedVersion }),
      setIsGenerating: (isGenerating) => set({ isGenerating }),

      // Sites
      sites: [],
      setSites: (sites) => set({ sites }),
    }),
    {
      name: 'zlandr-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        formData: state.formData,
        sessionId: state.sessionId,
        selectedVersion: state.selectedVersion,
      }),
    }
  )
);
